import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Métodos de Pago para Pedidos',
    seo_description: 'Aprende sobre métodos de pago, estados, su importancia en la gestión de pedidos y control del flujo de dinero en tu tienda en wicadu',
    seo_keywords:
      'métodos de pago, órdenes manuales, estados de pago, asistente de wicadu, propietarios de tienda, vendedores, pago en efectivo, tarjetas de crédito, transferencias bancarias, sistema de Control Interno, experiencia del cliente, seguimiento de transacciones, plataformas de pago, gestión de pedidos',
    page_id: PagesId.OrdersPaymentMethods,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
      payment_methods: 'Métodos de Pago para Pedidos',
    },
    article: {
      title: 'Métodos de Pago para Pedidos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para configurar <em>opciones de pago</em> y <em>gestión de la tienda</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST}>asistente de wicadu</a>.`,
        `<strong>Vendedores</strong> y <strong>Abastecedores</strong> con permisos para gestionar <em>inventario</em> y <em>modificar ordenes</em>.`,
      ],
      about_payment_methods: {
        title: '¿Qué son los métodos de pago y por qué son importantes?',
        seo_description:
          'Explica qué son los métodos de pago, como efectivo, tarjetas y transferencias, y su importancia para un seguimiento preciso y la experiencia del cliente.',
        content_type: ContentType.Paragraph,
        content: [
          'Los <strong>métodos de pago</strong> son las diferentes formas en que un cliente puede realizar un pago por una <em>compra</em>. Estos incluyen opciones como <strong>efectivo</strong>, <strong>tarjetas</strong> (crédito, débito o prepago) y <strong>transferencias bancarias</strong>, procesadas a menudo a través de una <strong>plataforma de pago</strong> (la pasarela de pago) que conecta al cliente con la tienda.',
          'Comprender qué <strong>método de pago</strong> utilizan tus clientes permite a la tienda registrar correctamente las <em>compras</em> y <em>ventas</em>, asegurando que los ingresos se reflejen con precisión. Además, este conocimiento ayuda a mejorar la <em>experiencia de tus clientes</em> al ofrecer opciones que se ajusten a sus preferencias, facilitando transacciones rápidas y sin complicaciones, mientras mantienes un control efectivo del flujo de dinero.',
        ],
      },
      payment_methods_in_manual_orders: {
        title: '¿Cómo funcionan los métodos de pago en órdenes manuales?',
        seo_description:
          'Detalla cómo funcionan los métodos de pago en órdenes manuales, incluyendo el registro de abonos y reglas para efectivo y tarjetas en el sistema de Control Interno.',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'En las <strong>órdenes manuales</strong>, ya sea para <em>compras</em> o <em>ventas</em>, se deben registrar los <strong>abonos</strong> (pagos) asociados a cada transacción. Cada abono incluye información como:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Proveedor o Pasarela de Pago: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: ['Quién realiza o recibe el pago.'],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Método de pago: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: ['La forma en que se realizó el pago.'],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Estado: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'Si el pago está <em>confirmado</em>, <em>en proceso</em> o <em>fallido</em>',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Fecha: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [' Cuándo se registró o espera confirmarse'],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Monto: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [' La cantidad pagada'],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Actualmente, en nuestro sistema de <strong>Control Interno</strong>, manejamos los siguientes <strong>métodos de pago</strong> con sus reglas específicas:',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold',
                    content: ['Efectivo: '],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'Solo admite el estado <strong>Confirmado</strong>.',
                            ],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            class: 'mb-10',
                            content: [
                              'No permite agregar ni adjuntar documentos, ya que se asume que el pago es inmediato y definitivo.',
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Tarjeta de Crédito</strong>, <strong>Tarjeta de Débito</strong>, <strong>Tarjeta Prepago</strong>, <strong>Transferencia Bancaria</strong> y <strong>No Identificado</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'Estos métodos ofrecen tres estados posibles: <strong>En Proceso</strong>, <strong>Fallido</strong> y <strong>Confirmado</strong>.',
                            ],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'Si el estado es <strong>En Proceso</strong>, puedes (opcionalmente) seleccionar una <em>fecha estimada de confirmación</em> y adjuntar documentos como comprobantes o recibos para respaldar el seguimiento.',
                            ],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'Los documentos no son obligatorios, pero ayudan a mantener un registro claro en caso de dudas o disputas.',
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      payment_statuses: {
        title: '¿Qué significa cada estado?',
        content_type: ContentType.Fragment,
        seo_description:
          'Describe el significado de los estados de pago: En Proceso, Fallido y Confirmado, y su papel en el seguimiento de pagos de pedidos.',
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Los <strong>estados</strong> reflejan el progreso o resultado de un pago. Aquí te explicamos qué significa cada uno:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['En Proceso: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'El pago está pendiente de confirmación. Puede estar en revisión, esperando autorización del banco o proveedor, o en trámite. Si aplicas una <em>fecha estimada de confirmación</em>, esta indica cuándo se espera que se resuelva.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Fallido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'El pago no se completó con éxito. Esto puede deberse a fondos insuficientes, un error en la transacción o rechazo por el proveedor. En este caso, se debe revisar y corregir la situación para intentar de nuevo.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Confirmado: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'El pago se ha procesado correctamente y los fondos están registrados. Para el <strong>efectivo</strong>, este es el único estado posible; para otros métodos, indica que la transacción está finalizada.',
                    ],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Entender estos <strong>estados</strong> te permite saber en qué punto está cada abono y actuar si es necesario, manteniendo tus <strong>órdenes manuales</strong> organizadas y bajo control.',
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'Payment Methods for Orders',
    seo_description: 'Learn about payment methods, statuses, their importance in order management, and cash flow control in your store on wicadu',
    seo_keywords:
      'payment methods, manual orders, payment statuses, wicadu Assistant, store owners, sellers, cash payments, credit cards, bank transfers, Internal Control system, customer experience, transaction tracking, payment platforms, order management',
    page_id: PagesId.OrdersPaymentMethods,
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
      payment_methods: 'Payment Methods for Orders',
    },
    article: {
      title: 'Payment Methods for Orders',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to configure <em>payment options</em> and <em>store operations</em> within <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Assistant</a>.`,
        '<strong>Sellers</strong> and <strong>Suppliers</strong> with permissions to manage <em>inventory</em> and <em>modify orders</em>.',
      ],
      about_payment_methods: {
        title: 'What are payment methods and why are they important?',
        content_type: ContentType.Paragraph,
        seo_description:
          'Explains what payment methods are, including cash, cards, and bank transfers, and their importance for accurate tracking and customer experience.',
        content: [
          '<strong>Payment methods</strong> are the different ways a customer can pay for a <em>purchase</em> or service. These include options like <strong>cash</strong>, <strong>credit cards</strong>, <strong>debit cards</strong>, <strong>prepaid cards</strong>, and <strong>bank transfers</strong>, often processed through a <strong>payment platform</strong> that connects the customer to the store',
          'Understanding which <strong>payment methods</strong> your customers use allows the store to accurately record <em>purchases</em> and <em>sales</em>, ensuring that revenue is properly reflected. Additionally, this knowledge helps enhance the <em>customer experience</em> by offering options that suit their preferences, enabling fast transactions while maintaining effective financial control.',
        ],
      },
      payment_methods_in_manual_orders: {
        title: 'How do payment methods work in Manual Orders?',
        seo_description:
          'Details how payment methods function in manual orders, including logging entries and rules for cash and cards in the Internal Control system.',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'In <strong>manual orders</strong>, whether <em>purchases</em> or <em>sales</em>, <strong>payments</strong> must be recorded for each transaction. Each payment includes the following details:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Payment processors or Payment gateways: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: ['The service processing the payment.'],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Payment method: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: ['The method used to make the payment.'],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Status: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'Whether the payment is <em>confirmed</em>, <em>in process</em>, or <em>failed</em>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Date: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'When it was recorded or when confirmation is expected',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Amount: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: ['The total paid'],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Currently, in our <strong>Internal Control</strong> system, we support the following <strong>payment methods</strong> with specific rules:',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold',
                    content: ['Cash: '],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'Only allows the <strong>Confirmed</strong> status.',
                            ],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            class: 'mb-10',
                            content: [
                              'No documents can be added or attached, as the payment is assumed to be immediate and final.',
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Credit Card</strong>, <strong>Debit Card</strong>, <strong>Prepaid Card</strong>, <strong>Bank Transfer</strong>, and <strong>Unidentified</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'These methods support three possible statuses: <strong>In Process</strong>, <strong>Failed</strong>, and <strong>Confirmed</strong>.',
                            ],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'If the status is <strong>In Process</strong>, you can (optionally) select an <em>estimated confirmation date</em> and attach documents such as receipts or invoices for tracking.',
                            ],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: [
                              'Documents are not required but help keep clear records in case of questions or disputes.',
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      payment_statuses: {
        title: 'What does each status mean?',
        seo_description:
          'Describes the meaning of payment statuses: In Process, Failed, and Confirmed, and their role in tracking order payments.',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Statuses</strong> show the progress or outcome of a payment. Here's what each one means:`,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['In Process: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'The payment is pending confirmation. It might be under review, awaiting bank or payment platform approval, or still in progress. If you set an <em>estimated confirmation date</em>, it shows when it’s expected to be resolved.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Failed: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'The payment was not successfully completed. This could be due to insufficient funds, a transaction error, or rejection by the payment processor. In this case, the issue should be reviewed and corrected before trying again.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold d-inline',
                    content: ['Confirmed: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'd-inline',
                    content: [
                      'The payment has been successfully processed and the funds are recorded. For <strong>cash</strong>, this is the only possible status; for other methods, it means the transaction is complete.',
                    ],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Understanding these <strong>statuses</strong> allows you to track each payment progress and take action if necessary, keeping your <strong>manual orders</strong> organized and under control.',
            ],
          },
        ],
      },
    },
  },
  fr: {
    seo_title: 'Modes de paiement pour les commandes',
    seo_description:
      'Découvrez les modes de paiement, leurs statuts et leur rôle dans la gestion des commandes et des flux financiers dans wicadu.',
    seo_keywords:
      'modes de paiement, paiements commande, statuts paiement, espèces, carte, virement, wicadu commandes',
    page_id: PagesId.OrdersPaymentMethods,
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
      payment_methods: 'Modes de paiement pour les commandes',
    },
    article: {
      title: 'Modes de paiement pour les commandes',
      intended: [
        `<strong>Propriétaires</strong> et <strong>administrateurs</strong> qui configurent les <em>options de paiement</em> et la <em>gestion de la boutique</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        `<strong>Vendeurs</strong> et équipes autorisés à gérer l’<em>inventaire</em> et à <em>mettre à jour des commandes</em>.`,
      ],
      about_payment_methods: {
        title: 'Que sont les modes de paiement et pourquoi sont-ils importants ?',
        seo_description:
          'Explique ce que sont les modes de paiement comme les espèces, les cartes et les virements, ainsi que leur importance pour le suivi des commandes.',
        content_type: ContentType.Paragraph,
        content: [
          'Les <strong>modes de paiement</strong> correspondent aux différentes façons dont un client peut régler une commande, par exemple en <strong>espèces</strong>, par <strong>carte</strong>, par <strong>virement bancaire</strong> ou via d’autres solutions internes utilisées dans <strong>wicadu</strong>. Leur bonne configuration est essentielle pour enregistrer correctement les <strong>paiements</strong> et garder un suivi fiable des <strong>commandes</strong>.',
          'Bien comprendre les modes de paiement vous permet de mieux suivre les encaissements, d’optimiser l’<strong>expérience client</strong>, de contrôler le statut des transactions et de garder une vision cohérente de vos flux financiers. C’est aussi une base importante pour la gestion des <strong>ventes manuelles</strong>, des <strong>achats manuels</strong> et des éventuels remboursements.',
        ],
      },
      payment_methods_in_manual_orders: {
        title: 'Comment fonctionnent les modes de paiement dans les commandes manuelles ?',
        seo_description:
          'Décrit le fonctionnement des paiements dans les commandes manuelles, y compris les informations à enregistrer.',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Dans les <strong>commandes manuelles</strong>, chaque paiement doit être enregistré avec ses informations principales.',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Fournisseur ou passerelle de paiement : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['qui envoie ou reçoit le paiement.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Mode de paiement : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['la forme utilisée pour régler la commande.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Statut : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['si le paiement est confirmé, en cours ou échoué.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Date : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['la date d’enregistrement ou de confirmation attendue.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Montant : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['la somme réellement payée.'] },
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ces données aident à suivre la progression des encaissements et à maintenir l’ordre dans vos opérations.',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Strong,
                    class: 'bold',
                    content: ['Espèces : '],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: ['Ce mode n’accepte généralement qu’un statut <strong>Confirmé</strong>.'],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            class: 'mb-10',
                            content: ['Il est utilisé lorsque le paiement est immédiat et définitivement reçu.'],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Carte de crédit</strong>, <strong>carte de débit</strong>, <strong>carte prépayée</strong>, <strong>virement bancaire</strong> et <strong>non identifié</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: ['Ces méthodes peuvent utiliser les statuts <strong>En cours</strong>, <strong>Échoué</strong> ou <strong>Confirmé</strong>.'],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: ['Si le paiement est encore en cours, vous pouvez ajouter une date de confirmation estimée et des justificatifs.'],
                          },
                        ],
                      },
                      {
                        content_type: ContentType.ListItem,
                        content: [
                          {
                            content_type: ContentType.Paragraph,
                            content: ['Les documents restent facultatifs, mais ils facilitent le suivi et la résolution de problèmes.'],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      payment_statuses: {
        title: 'États de paiement',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['En cours : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['le paiement a été initié mais n’est pas encore confirmé.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Échoué : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['la transaction n’a pas abouti et doit être corrigée ou remplacée.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Confirmé : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['le paiement est validé et peut être comptabilisé comme encaissé.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Strong, class: 'bold d-inline', content: ['Suivi : '] },
                  { content_type: ContentType.Paragraph, class: 'd-inline', content: ['chaque statut aide l’équipe à savoir si la commande peut continuer ou si une action reste nécessaire.'] },
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Comprendre ces <strong>statuts de paiement</strong> vous permet de savoir quelles commandes peuvent avancer, lesquelles nécessitent encore une action et quand il faut ajouter des documents ou mettre à jour les encaissements.',
            ],
          },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
